import { useState, type FormEvent, type ReactNode } from 'react';
import { useParams } from 'react-router-dom';
import { OrgNav } from '../components/OrgNav.js';
import { Card, CopyLink, EmptyState, ErrorBanner, Loading, Spinner } from '../components/ui.js';
import {
  api,
  type JoinTicket,
  type LiveSession,
  type MeetingCredential,
  type Organization,
} from '../lib/api.js';
import { formatDate, formatRelative } from '../lib/format.js';
import { useAction, useAsync } from '../lib/hooks.js';

/**
 * Live sessions.
 *
 * Three tabs, and the server decides which session belongs in which: `state` is
 * derived there from the schedule and the provider's own start/end signals, so a
 * class that began late and is overrunning arrives as `live` and this page does
 * no clock arithmetic of its own.
 *
 * Managers get the scheduling form and the provider setup; members get the list
 * and the join button. The server enforces both regardless.
 */

/**
 * Two tabs, not three.
 *
 * "What can I join, and what is next?" is one question, so live and upcoming
 * share a tab and the live ones are lifted to the top of it. Splitting them
 * meant the ordinary state of this page was two empty lists and a reader left to
 * reconcile them — someone opening a "Live now" tab twenty minutes before class
 * saw a dead end.
 */
const TABS: ReadonlyArray<{ view: 'agenda' | 'past'; label: string; empty: string }> = [
  {
    view: 'agenda',
    label: 'Upcoming',
    empty: 'Nothing scheduled yet. Schedule your first class above.',
  },
  { view: 'past', label: 'Past', empty: 'No classes have finished yet.' },
];

export function LiveSessionsPage(): ReactNode {
  const { orgId = '' } = useParams();
  const [tab, setTab] = useState<'agenda' | 'past'>('agenda');

  const org = useAsync(() => api.get<Organization>(`/api/organizations/${orgId}`), [orgId]);
  const sessions = useAsync(
    () =>
      api.get<{ data: LiveSession[]; pagination: { total: number } }>(
        `/api/organizations/${orgId}/live-sessions?state=${tab}&limit=100`,
      ),
    [orgId, tab],
  );
  /*
   * The rules behind the classes, so a repeating term can be shown as one line
   * instead of ten identical ones. Fetched separately because a session carries
   * only its `seriesId` — the summary a person reads lives on the series.
   */
  const series = useAsync(
    () => api.get<{ data: SeriesSummary[] }>(`/api/organizations/${orgId}/live-session-series`),
    [orgId],
  );
  const seriesById = new Map((series.data?.data ?? []).map((row) => [row.id, row]));

  if (org.loading) return <Loading />;

  const canManage = org.data?.role === 'owner' || org.data?.role === 'admin';

  const rows = sessions.data?.data ?? [];
  // The band holds only what has genuinely begun. A session inside the ten
  // minutes before its start is joinable and arrives as `live`, but calling it
  // "on now" before the host has started would be untrue.
  const live = tab === 'agenda' ? rows.filter(hasBegun) : [];
  const rest = rows.filter((session) => !live.includes(session));
  const active = TABS.find((entry) => entry.view === tab);

  return (
    <div className="container">
      <div className="page-head">
        <div>
          <h1>Classes</h1>
          <p>Everything your students can join, and the codes that let them in.</p>
        </div>
        <div className="page-head-actions">
          <button type="button" className="button-secondary" onClick={sessions.reload}>
            Refresh
          </button>
        </div>
      </div>

      <OrgNav orgId={orgId} />

      <div className="stack">
        <ErrorBanner error={sessions.error} />

        {/* The three tabs, as their own pill group beneath the section nav. */}
        <nav className="tabs" aria-label="Session state">
          {TABS.map((entry) => (
            <button
              key={entry.view}
              type="button"
              className={`tab${tab === entry.view ? ' active' : ''}`}
              aria-current={tab === entry.view}
              onClick={() => setTab(entry.view)}
            >
              {entry.label}
            </button>
          ))}
        </nav>

        {canManage && <ScheduleForm orgId={orgId} onScheduled={sessions.reload} />}

        {sessions.loading ? (
          <Card>
            <Spinner />
          </Card>
        ) : rows.length === 0 ? (
          <Card>
            <EmptyState title="No classes found">
              <p className="muted small">{active?.empty}</p>
            </EmptyState>
          </Card>
        ) : (
          <>
            {/*
              Lifted out of the list, because it is the only thing on the page
              that can be acted on right now. The server already sorts these
              first; this makes that visible.
            */}
            {live.length > 0 && (
              <Card title="On now">
                {live.map((session) => (
                  <div key={session.id} className="live-band">
                    <SessionRow
                      orgId={orgId}
                      session={session}
                      canManage={canManage}
                      onChanged={sessions.reload}
                    />
                  </div>
                ))}
              </Card>
            )}

            {rest.length > 0 && (
              <Card title={tab === 'past' ? 'Past' : 'Coming up'}>
                {/*
                  A term is one entry, not one per week.

                  Ten Mondays-and-Thursdays used to fill the page with ten
                  identical titles under ten date headings, which buries every
                  other class the institute runs. Collapsed, the term reads as
                  what a teacher scheduled: one thing, repeating, with its next
                  date — and it opens if they want the individual weeks.
                */}
                {collapseSeries(rest).map((entry) =>
                  entry.kind === 'series' ? (
                    <SeriesGroupRow
                      key={entry.seriesId}
                      orgId={orgId}
                      summary={seriesById.get(entry.seriesId)}
                      sessions={entry.sessions}
                      canManage={canManage}
                      onChanged={() => {
                        sessions.reload();
                        series.reload();
                      }}
                    />
                  ) : (
                    <div key={entry.label}>
                      <p className="day-heading">{entry.label}</p>
                      {entry.sessions.map((session) => (
                        <SessionRow
                          key={session.id}
                          orgId={orgId}
                          session={session}
                          canManage={canManage}
                          onChanged={sessions.reload}
                        />
                      ))}
                    </div>
                  ),
                )}
              </Card>
            )}
          </>
        )}

        {canManage && <ProviderCard orgId={orgId} />}
      </div>
    </div>
  );
}

interface ClassCode {
  id: string;
  code: string;
  status: string;
  usedCount: number;
  maxUses: number | null;
  /** Which class it was created from. Null for an institute-wide code. */
  liveSessionId: string | null;
  issuedToEmail: string | null;
  redeemedByEmail: string | null;
}

/**
 * Codes for this class, where the class is.
 *
 * They used to live on the Members page, which is where you go to think about
 * people rather than about a lesson. A teacher generating codes has a class in
 * mind — this one, the one on screen — so the tool belongs next to it and the
 * class is chosen by where you clicked rather than from a dropdown.
 *
 * Collapsed by default. Most visits to this page are to see what is on, not to
 * hand out codes, and an always-open panel on every row would bury the schedule.
 */
function ClassCodes({
  orgId,
  session,
  coveredSessionIds,
  coversSeries = false,
}: {
  orgId: string;
  session: LiveSession;
  /**
   * The classes whose codes this panel is about.
   *
   * One id for a single class; every occurrence for a term, because a code
   * issued against any one of them admits the student to all of them. Passed in
   * rather than derived here: the panel is handed the next occurrence to
   * generate against and cannot see its siblings.
   */
  coveredSessionIds: readonly string[];
  coversSeries?: boolean;
}): ReactNode {
  const [open, setOpen] = useState(false);
  const [count, setCount] = useState('40');
  const [generated, setGenerated] = useState<ClassCode[]>([]);
  const { run, pending, error } = useAction();

  const existing = useAsync(
    async () =>
      open
        ? await api.get<{ data: ClassCode[] }>(`/api/organizations/${orgId}/join-codes`)
        : { data: [] },
    [orgId, open],
  );

  /*
   * Only the codes for the classes this panel covers.
   *
   * The endpoint returns the whole institute's, and the filter that used to sit
   * here was `issuedToEmail !== null || true` — always true, so a term with
   * forty codes reported the institute's fifty-three. A number that looks
   * specific and is not is worse than no number: it was on screen next to a
   * teacher's own term, inviting them to trust every other figure on the page.
   */
  const mine = (existing.data?.data ?? []).filter(
    (row) => row.liveSessionId !== null && coveredSessionIds.includes(row.liveSessionId),
  );

  async function generate(): Promise<void> {
    await run(async () => {
      const response = await api.post<{ data: ClassCode[] }>(
        `/api/organizations/${orgId}/join-codes/batch`,
        { count: Number(count), liveSessionId: session.id },
      );
      setGenerated(response.data);
      existing.reload();
    });
  }

  const asCodes = generated.map((row) => row.code).join('\n');
  const asLinks = generated.map((row) => `${window.location.origin}/join/${row.code}`).join('\n');

  function download(): void {
    const body =
      'code,link\n' +
      generated.map((row) => `${row.code},${window.location.origin}/join/${row.code}`).join('\n');
    const url = URL.createObjectURL(new Blob([body], { type: 'text/csv;charset=utf-8' }));
    const anchorEl = document.createElement('a');
    anchorEl.href = url;
    anchorEl.download = `codes-${session.title.replace(/[^a-z0-9]+/gi, '-').toLowerCase()}.csv`;
    anchorEl.click();
    URL.revokeObjectURL(url);
  }

  if (!open) {
    return (
      <div style={{ marginTop: '0.5rem' }}>
        <button type="button" onClick={() => setOpen(true)}>
          Codes for this {coversSeries ? 'term' : 'class'}
        </button>
      </div>
    );
  }

  return (
    <div
      style={{
        marginTop: '0.6rem',
        border: '1px solid var(--border)',
        borderRadius: '8px',
        padding: '0.75rem',
      }}
    >
      <div className="row">
        <strong className="small">Codes for this {coversSeries ? 'term' : 'class'}</strong>
        <button type="button" style={{ marginLeft: 'auto' }} onClick={() => setOpen(false)}>
          Close
        </button>
      </div>

      <p className="field-hint">
        One code per student, each usable once.
        {coversSeries
          ? ' A code admits them to every class in this term, so they need it only once.'
          : ' Hand them out however you like — a group chat, a printed slip.'}
      </p>

      <ErrorBanner message={error} />
      <ErrorBanner error={existing.error} />

      <div className="row" style={{ alignItems: 'flex-end', gap: '0.75rem' }}>
        <div>
          <label htmlFor={`codes-${session.id}`}>How many?</label>
          <input
            id={`codes-${session.id}`}
            type="number"
            min={1}
            max={400}
            value={count}
            onChange={(e) => setCount(e.target.value)}
            style={{ width: '7rem' }}
          />
        </div>
        <button type="button" className="button-primary" disabled={pending} onClick={() => void generate()}>
          {pending && <Spinner />}
          Generate
        </button>
        <span className="field-hint">Up to 400 at a time.</span>
      </div>

      {generated.length > 0 && (
        <div className="stack" style={{ marginTop: '0.75rem' }}>
          <p className="small">
            <strong>{generated.length}</strong> codes generated. Copy them now or download the
            file — they stay listed below.
          </p>
          <div className="button-row">
            <CopyLink value={asCodes} label="Copy all codes" />
            <CopyLink value={asLinks} label="Copy all links" />
            <button type="button" onClick={download}>
              Download CSV
            </button>
          </div>
          <textarea readOnly rows={5} value={asCodes} className="mono" />
        </div>
      )}

      {existing.loading ? (
        <Spinner />
      ) : mine.length > 0 ? (
        <p className="small faint" style={{ marginTop: '0.5rem' }}>
          {/*
            A count rather than the whole register. Who claimed which is on the
            Members page, which is where a teacher goes to look at people; here
            they are handing codes out.
          */}
          {mine.filter((c) => c.redeemedByEmail !== null).length} of {mine.length} codes for this{' '}
          {coversSeries ? 'term' : 'class'} have been claimed.
        </p>
      ) : null}
    </div>
  );
}

interface SeriesSummary {
  id: string;
  title: string;
  summary: string;
  occurrenceCount: number;
  stoppedBy: string;
  canceledAt: string | null;
}

/** A day of one-off classes, or a whole repeating term. */
type ListEntry =
  | { kind: 'day'; label: string; sessions: LiveSession[] }
  | { kind: 'series'; seriesId: string; sessions: LiveSession[] };

/**
 * Fold each repeating term into a single entry, leaving everything else by day.
 *
 * Order is preserved: a term takes the position of its earliest occurrence, so a
 * class next Tuesday still appears before a term starting in October. Grouping
 * by series *first* and then by day would have shuffled the page around.
 *
 * A series with only one occurrence left is not collapsed — there is nothing to
 * collapse, and a lone class hidden behind a disclosure is worse than a lone
 * class.
 */
function collapseSeries(sessions: LiveSession[]): ListEntry[] {
  const bySeries = new Map<string, LiveSession[]>();
  for (const session of sessions) {
    if (!session.seriesId) continue;
    const held = bySeries.get(session.seriesId);
    if (held) held.push(session);
    else bySeries.set(session.seriesId, [session]);
  }

  const entries: ListEntry[] = [];
  const emitted = new Set<string>();

  for (const session of sessions) {
    const group = session.seriesId ? bySeries.get(session.seriesId) : undefined;

    if (session.seriesId && group && group.length > 1) {
      if (emitted.has(session.seriesId)) continue;
      emitted.add(session.seriesId);
      entries.push({ kind: 'series', seriesId: session.seriesId, sessions: group });
      continue;
    }

    const label = dayLabel(session.startsAt);
    const last = entries[entries.length - 1];
    if (last && last.kind === 'day' && last.label === label) last.sessions.push(session);
    else entries.push({ kind: 'day', label, sessions: [session] });
  }

  return entries;
}

/**
 * A repeating term, collapsed.
 *
 * Shows the next occurrence rather than the first, because "when is this next"
 * is the question a teacher looking at a term actually has. Cancelling the term
 * lives here too: doing it from a single week would be ambiguous, and cancelling
 * ten weeks one at a time is not a workflow.
 */
function SeriesGroupRow({
  orgId,
  summary,
  sessions,
  canManage,
  onChanged,
}: {
  orgId: string;
  summary: SeriesSummary | undefined;
  sessions: LiveSession[];
  canManage: boolean;
  onChanged: () => void;
}): ReactNode {
  const [open, setOpen] = useState(false);
  const { run, pending, error } = useAction();

  const ordered = [...sessions].sort((a, b) => a.startsAt.localeCompare(b.startsAt));
  const next = ordered[0];
  if (!next) return null;

  async function cancelTerm(): Promise<void> {
    await run(async () => {
      await api.post(
        `/api/organizations/${orgId}/live-session-series/${next!.seriesId ?? ''}/cancel`,
      );
      onChanged();
    });
  }

  return (
    <div className="usage-row">
      {/*
        Wrapping, because `.row` does not. A long title plus two badges plus two
        buttons overflowed the card at a normal window width and pushed "Cancel
        term" off the right edge — with no horizontal scrollbar on the card to
        get it back. `minWidth: 0` lets the text block shrink instead of forcing
        the row wider than its parent.
      */}
      <div className="row">
        <div style={{ minWidth: 0 }}>
          <div className="row" style={{ gap: '0.5rem' }}>
            <strong>{summary?.title ?? next.title}</strong>
            <span className="badge">Repeats</span>
            <span className="badge">{next.kind === 'zoom' ? 'Zoom' : 'YouTube'}</span>
          </div>
          <p className="small muted" style={{ marginTop: '0.25rem' }}>
            {summary?.summary ?? 'Repeating'} · {ordered.length} still to come
            {summary && summary.occurrenceCount !== ordered.length
              ? ` of ${String(summary.occurrenceCount)}`
              : ''}
          </p>
          <p className="small faint">
            Next: {formatDate(next.startsAt)} · {timeOf(next.startsAt)}–{timeOf(next.endsAt)}
            {next.state === 'upcoming' && ` · starts ${formatRelative(next.startsAt)}`}
          </p>
        </div>

        <div className="button-row" style={{ marginLeft: 'auto' }}>
          <button type="button" onClick={() => setOpen(!open)}>
            {open ? 'Hide classes' : `Show ${String(ordered.length)} classes`}
          </button>
          {canManage && (
            <button
              type="button"
              className="button-danger"
              disabled={pending}
              onClick={() => void cancelTerm()}
            >
              {pending && <Spinner />}
              Cancel term
            </button>
          )}
        </div>
      </div>

      <ErrorBanner message={error} />

      {/*
        Codes belong with the term, not with one week of it. A code issued
        against any occurrence admits the student to the whole series, so this is
        the right place for it — and the wrong place is a separate page.
      */}
      {canManage && (
        <ClassCodes
          orgId={orgId}
          session={next}
          coveredSessionIds={ordered.map((occurrence) => occurrence.id)}
          coversSeries
        />
      )}

      {open && (
        <div style={{ marginTop: '0.75rem', borderTop: '1px solid var(--border)' }}>
          {ordered.map((session) => (
            <SessionRow
              key={session.id}
              orgId={orgId}
              session={session}
              canManage={canManage}
              onChanged={onChanged}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function SessionRow({
  orgId,
  session,
  canManage,
  onChanged,
}: {
  orgId: string;
  session: LiveSession;
  canManage: boolean;
  onChanged: () => void;
}): ReactNode {
  const { run, pending, error } = useAction();
  const [ticket, setTicket] = useState<JoinTicket | null>(null);
  const [gated, setGated] = useState(false);
  const [shared, setShared] = useState<{ code: string; label: string } | null>(null);

  async function join(): Promise<void> {
    await run(async () => {
      const response = await api.post<{ joinTicket: JoinTicket | null; adGate: unknown }>(
        `/api/organizations/${orgId}/live-sessions/${session.id}/join?platform=web`,
      );

      /*
       * An advertisement gate instead of a ticket. Staff never see this — hosts
       * and admins are exempt — so it only appears for a member signed in here
       * rather than in the app. Rewarded video is an app surface, so this says so
       * plainly instead of opening a window with nothing in it.
       */
      if (!response.joinTicket) {
        setGated(true);
        setTicket(null);
        return;
      }

      setGated(false);
      setTicket(response.joinTicket);
      // Zoom opens in its own app or a new tab; YouTube opens the watch page.
      const target = response.joinTicket.joinUrl ?? response.joinTicket.watchUrl;
      if (target) window.open(target, '_blank', 'noopener,noreferrer');
    });
  }

  /**
   * Make a code for this class.
   *
   * The code is *created from* the class and named after it, which is how a
   * teacher thinks of it — but redeeming it subscribes the learner to the whole
   * institute. They see next week's class too, because a subscriber to a teacher
   * is what they are. The link exists so "which class brought these students" has
   * an answer.
   */
  async function share(): Promise<void> {
    await run(async () => {
      const code = await api.post<{ code: string; label: string }>(
        `/api/organizations/${orgId}/join-codes`,
        { liveSessionId: session.id },
      );
      setShared(code);
    });
  }

  async function cancel(): Promise<void> {
    await run(async () => {
      await api.post(`/api/organizations/${orgId}/live-sessions/${session.id}/cancel`);
      onChanged();
    });
  }

  async function remove(): Promise<void> {
    await run(async () => {
      await api.delete(`/api/organizations/${orgId}/live-sessions/${session.id}`);
      onChanged();
    });
  }

  return (
    <div className="usage-row">
      <div className="row">
        <div>
          <div className="row" style={{ gap: '0.5rem' }}>
            <strong>{session.title}</strong>
            <StateBadge session={session} />
            <span className="badge">{session.kind === 'zoom' ? 'Zoom' : 'YouTube'}</span>
            {session.status === 'draft' && <span className="badge">Draft</span>}
          </div>
          <p className="small muted" style={{ marginTop: '0.25rem' }}>
            {formatDate(session.startsAt)} · {timeOf(session.startsAt)}–{timeOf(session.endsAt)} ·{' '}
            {session.durationMinutes} min
            {session.state === 'upcoming' && ` · starts ${formatRelative(session.startsAt)}`}
          </p>
          {session.description && <p className="small faint">{session.description}</p>}
        </div>

        <div className="button-row" style={{ marginLeft: 'auto' }}>
          {session.joinable && (
            <button
              type="button"
              className="button-primary"
              disabled={pending}
              onClick={() => void join()}
            >
              {pending && <Spinner />}
              Join
            </button>
          )}
          {/*
            Only for a class people can still join. A code for a finished or
            cancelled class is a code that admits people to nothing they came for.
          */}
          {canManage && session.status === 'scheduled' && session.state !== 'past' && (
            <button
              type="button"
              className="button-secondary"
              disabled={pending}
              onClick={() => void share()}
            >
              Share code
            </button>
          )}
          {canManage && session.status === 'draft' && (
            <button
              type="button"
              className="button-danger"
              disabled={pending}
              onClick={() => void remove()}
            >
              Delete
            </button>
          )}
          {canManage && session.status === 'scheduled' && session.state !== 'past' && (
            <button
              type="button"
              className="button-danger"
              disabled={pending}
              onClick={() => void cancel()}
            >
              Cancel
            </button>
          )}
        </div>
      </div>

      <ErrorBanner message={error} />

      {/* Only for a class that is not part of a term: a term carries its own
          panel, and two on one screen would be two answers to one question. */}
      {canManage &&
        session.seriesId === null &&
        session.status === 'scheduled' &&
        session.state !== 'past' && (
          <ClassCodes orgId={orgId} session={session} coveredSessionIds={[session.id]} />
        )}

      {shared && (
        <div className="banner" style={{ marginTop: '0.6rem' }}>
          <p className="small" style={{ marginBottom: '0.4rem' }}>
            Share this with your students. Anyone who enters it subscribes to{' '}
            <strong>all</strong> your classes, not only this one.
          </p>
          {/* Large and monospaced: this gets read aloud and copied by hand. */}
          <p
            className="mono"
            style={{ fontSize: '1.4rem', letterSpacing: '0.1em', margin: '0.2rem 0' }}
          >
            {shared.code}
          </p>
          <div className="button-row">
            <CopyLink value={shared.code} label="Copy code" />
            <CopyLink
              value={`${window.location.origin}/join/${shared.code}`}
              label="Copy link"
            />
          </div>
        </div>
      )}

      {/*
        Shown after joining so a host can pass the details on, or reopen the
        window they closed. The server hands these out only here.
      */}
      {gated && (
        <div className="banner banner-warning" style={{ marginTop: '0.5rem' }}>
          <div className="small">
            Joining this session needs a short advertisement first. Open it in the RioPlay Live app
            to watch it and join.
          </div>
        </div>
      )}

      {ticket && (
        <div className="banner banner-success" style={{ marginTop: '0.5rem' }}>
          <div className="small">
            Joining as {ticket.displayName}
            {ticket.role === 1 ? ' (host)' : ''}
            {ticket.meetingNumber ? ` · meeting ${ticket.meetingNumber}` : ''}
            {ticket.passcode ? ` · passcode ${ticket.passcode}` : ''}
          </div>
          {(ticket.joinUrl ?? ticket.watchUrl) && (
            <div style={{ marginTop: '0.4rem' }}>
              <CopyLink value={ticket.joinUrl ?? ticket.watchUrl ?? ''} label="Copy link" />
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/**
 * Whether the host has actually started.
 *
 * The server opens joining ten minutes before the scheduled start, so a session
 * arrives as `live` slightly before anyone is in the room. `actualStartedAt` is
 * the only honest signal that it is genuinely under way — everything else is the
 * calendar.
 */
function hasBegun(session: LiveSession): boolean {
  if (session.state !== 'live') return false;
  if (session.actualStartedAt !== null) return true;
  // No provider signal (a YouTube session has none), so fall back to the
  // scheduled start having passed.
  return new Date(session.startsAt).getTime() <= Date.now();
}

function StateBadge({ session }: { session: LiveSession }): ReactNode {
  if (session.state === 'canceled') return <span className="badge badge-danger">Cancelled</span>;
  if (session.state === 'past') return <span className="badge">Finished</span>;
  if (session.state === 'live') {
    return hasBegun(session) ? (
      <span className="badge badge-success">
        <span className="live-dot" aria-hidden="true" />
        Live now
      </span>
    ) : (
      // Joinable, but nobody has started it. Saying "Live now" here would be a
      // small lie the student would notice on arriving to an empty room.
      <span className="badge badge-warning">Starting soon</span>
    );
  }
  return <span className="badge badge-warning">Upcoming</span>;
}


/**
 * Group by calendar day, because that is how a schedule is read.
 *
 * Order is preserved from the server, so this never re-sorts — it only inserts
 * headings where the day changes.
 */

function dayLabel(iso: string): string {
  const date = new Date(iso);
  const midnight = (d: Date): number =>
    new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
  const days = Math.round((midnight(date) - midnight(new Date())) / 86_400_000);
  if (days === 0) return 'Today';
  if (days === 1) return 'Tomorrow';
  if (days === -1) return 'Yesterday';
  return date.toLocaleDateString(undefined, { weekday: 'short', day: 'numeric', month: 'short' });
}

/** Local-time HH:MM, since a session's own timezone is shown separately. */
function timeOf(iso: string): string {
  return new Date(iso).toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' });
}

/**
 * The repeat rule as a sentence.
 *
 * Mirrors what the server will store as its summary, deliberately: a publisher
 * who reads "Every 2 weeks on Sunday, 12 classes" here and sees the same words on
 * the class afterwards knows nothing was lost in translation.
 */
function describeRepeat(
  frequency: 'daily' | 'weekly' | 'monthly',
  interval: number,
  weekdays: number[],
  count: number,
  startsAt: string,
): string {
  const unit =
    frequency === 'daily'
      ? interval === 1
        ? 'day'
        : 'days'
      : frequency === 'weekly'
        ? interval === 1
          ? 'week'
          : 'weeks'
        : interval === 1
          ? 'month'
          : 'months';
  const every = interval === 1 ? `Every ${unit}` : `Every ${String(interval)} ${unit}`;

  const names = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
  const start = new Date(startsAt);
  const days =
    frequency !== 'weekly'
      ? ''
      : weekdays.length > 0
        ? ` on ${[...weekdays].sort((a, b) => a - b).map((d) => names[d]).join(', ')}`
        : Number.isNaN(start.getTime())
          ? ''
          : ` on ${names[start.getDay()] ?? ''}`;

  const total = count > 1 ? `, ${String(count)} classes` : '';
  const from = Number.isNaN(start.getTime())
    ? ''
    : `, starting ${start.toLocaleDateString(undefined, { day: 'numeric', month: 'short' })}`;

  return `${every}${days}${total}${from}. One Zoom meeting reused, so a student needs a code only once.`;
}

/* ------------------------------------------------------------------- create */

function ScheduleForm({
  orgId,
  onScheduled,
}: {
  orgId: string;
  onScheduled: () => void;
}): ReactNode {
  const { run, pending, error } = useAction();
  const [open, setOpen] = useState(false);
  const [kind, setKind] = useState<'zoom' | 'youtube'>('zoom');
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [startsAt, setStartsAt] = useState(defaultStart());
  const [durationMinutes, setDurationMinutes] = useState('60');
  const [capacity, setCapacity] = useState('');
  const [youtubeUrl, setYoutubeUrl] = useState('');
  const [meeting, setMeeting] = useState('');
  const [meetingPasscode, setMeetingPasscode] = useState('');
  const [zoomHostUserId, setZoomHostUserId] = useState('');
  const [repeats, setRepeats] = useState(false);
  const [frequency, setFrequency] = useState<'daily' | 'weekly' | 'monthly'>('weekly');
  const [interval, setInterval] = useState('1');
  const [weekdays, setWeekdays] = useState<number[]>([]);
  const [count, setCount] = useState('12');

  async function submit(event: FormEvent): Promise<void> {
    event.preventDefault();
    await run(async () => {
      const start = new Date(startsAt);
      const end = new Date(start.getTime() + Number(durationMinutes) * 60_000);
      await api.post(`/api/organizations/${orgId}/live-sessions`, {
        title,
        kind,
        description: description.trim() === '' ? null : description,
        startsAt: start.toISOString(),
        endsAt: end.toISOString(),
        // The browser's zone is the one the person scheduling is thinking in.
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        capacity: capacity === '' ? null : Number(capacity),
        ...(kind === 'youtube' ? { youtubeUrl } : {}),
        /*
         * Only sent when the publisher actually filled them in. An empty
         * `meeting` would be a different instruction from an absent one: absent
         * means "create a meeting for me", empty would be a validation error on a
         * field they never touched.
         */
        ...(kind === 'zoom' && meeting.trim() !== ''
          ? {
              meeting: meeting.trim(),
              meetingPasscode: meetingPasscode.trim() === '' ? null : meetingPasscode.trim(),
              zoomHostUserId: zoomHostUserId.trim() === '' ? null : zoomHostUserId.trim(),
            }
          : {}),
        /*
         * Absent when the class does not repeat. The server reads the field's
         * presence as "this is a series", so sending an empty object would create
         * one occurrence and a series row describing nothing.
         */
        ...(repeats
          ? {
              repeat: {
                frequency,
                interval: Number(interval),
                // Omitted for daily and monthly, which the server refuses to
                // accept weekdays for at all.
                ...(frequency === 'weekly' && weekdays.length > 0 ? { weekdays } : {}),
                count: Number(count),
              },
            }
          : {}),
      });
      setTitle('');
      setDescription('');
      setYoutubeUrl('');
      setMeeting('');
      setMeetingPasscode('');
      setRepeats(false);
      setWeekdays([]);
      // zoomHostUserId is deliberately kept: the same teacher hosts the next
      // class, and retyping their Zoom address for every session is the kind of
      // friction that makes people leave it blank — which silently costs
      // in-app joining on Windows.
      setOpen(false);
      onScheduled();
    });
  }

  if (!open) {
    return (
      <Card>
        <div className="row">
          <div>
            <strong>Schedule a class</strong>
            <p className="small faint">
              Paste your own Zoom meeting, have one created for you, or paste a YouTube link.
            </p>
          </div>
          <button
            type="button"
            className="button-primary"
            style={{ marginLeft: 'auto' }}
            onClick={() => setOpen(true)}
          >
            Schedule a class
          </button>
        </div>
      </Card>
    );
  }

  return (
    <Card title="Schedule a class">
      <form onSubmit={(e) => void submit(e)} className="stack">
        <ErrorBanner message={error} />

        <div className="row" style={{ alignItems: 'flex-end', gap: '0.75rem' }}>
          <div style={{ flex: 2 }}>
            <label htmlFor="ls-title">Title</label>
            <input
              id="ls-title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              maxLength={200}
              required
            />
          </div>
          <div>
            <label htmlFor="ls-kind">Platform</label>
            <select
              id="ls-kind"
              value={kind}
              onChange={(e) => setKind(e.target.value as 'zoom' | 'youtube')}
              style={{ width: 'auto' }}
            >
              <option value="zoom">Zoom meeting</option>
              <option value="youtube">YouTube Live</option>
            </select>
          </div>
        </div>

        {kind === 'zoom' && (
          <>
            <div>
              <label htmlFor="ls-meeting">Zoom meeting (optional)</label>
              <input
                id="ls-meeting"
                value={meeting}
                onChange={(e) => setMeeting(e.target.value)}
                placeholder="Paste the invitation link, or the meeting number"
              />
              <p className="field-hint">
                Leave this empty and a meeting is created for you — which needs Zoom credentials
                configured below. Fill it in and nothing is asked of Zoom: paste what “Copy
                Invitation” gives you, or the 9–11 digit number.
              </p>
              {meeting.trim() !== '' && (
                <p className="field-hint">
                  A meeting you created sends us no webhooks, so “Live now” follows the schedule
                  rather than the room. A class that starts late will still read as live.
                </p>
              )}
            </div>

            {meeting.trim() !== '' && (
              <div className="row" style={{ alignItems: 'flex-end', gap: '0.75rem' }}>
                <div style={{ flex: 1 }}>
                  <label htmlFor="ls-passcode">Passcode</label>
                  <input
                    id="ls-passcode"
                    value={meetingPasscode}
                    onChange={(e) => setMeetingPasscode(e.target.value)}
                    placeholder="The one people type"
                  />
                  <p className="field-hint">
                    Not the <code>pwd</code> in the link — that is read from the link itself.
                  </p>
                </div>
                <div style={{ flex: 1 }}>
                  <label htmlFor="ls-host">Zoom host</label>
                  <input
                    id="ls-host"
                    value={zoomHostUserId}
                    onChange={(e) => setZoomHostUserId(e.target.value)}
                    placeholder="teacher@yourdomain.com"
                  />
                  <p className="field-hint">
                    The email of whoever hosts this in your Zoom account. Without it, Windows
                    learners open the Zoom app instead of joining inside RioPlay.
                  </p>
                </div>
              </div>
            )}
          </>
        )}

        {kind === 'youtube' && (
          <div>
            <label htmlFor="ls-youtube">YouTube link</label>
            <input
              id="ls-youtube"
              value={youtubeUrl}
              onChange={(e) => setYoutubeUrl(e.target.value)}
              placeholder="https://www.youtube.com/watch?v=…"
              required
            />
            <p className="field-hint">
              Create the broadcast in YouTube Studio, then paste its watch, live or share link.
            </p>
          </div>
        )}

        <div className="row" style={{ alignItems: 'flex-end', gap: '0.75rem' }}>
          <div style={{ flex: 1 }}>
            <label htmlFor="ls-start">Starts</label>
            <input
              id="ls-start"
              type="datetime-local"
              value={startsAt}
              onChange={(e) => setStartsAt(e.target.value)}
              required
            />
          </div>
          <div>
            <label htmlFor="ls-duration">Minutes</label>
            <input
              id="ls-duration"
              type="number"
              min={1}
              max={1440}
              value={durationMinutes}
              onChange={(e) => setDurationMinutes(e.target.value)}
              required
            />
          </div>
          <div>
            <label htmlFor="ls-capacity">Expected attendance</label>
            <input
              id="ls-capacity"
              type="number"
              min={1}
              value={capacity}
              onChange={(e) => setCapacity(e.target.value)}
              placeholder="optional"
            />
            <p className="field-hint">Checked against your plan.</p>
          </div>
        </div>

        {/*
          Repeating, which is what a batch actually is. Off by default: a one-off
          class is the simpler case and a term is four more decisions.
        */}
        <div style={{ border: '1px solid var(--border)', borderRadius: '8px', padding: '0.75rem' }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <input
              type="checkbox"
              checked={repeats}
              onChange={(e) => setRepeats(e.target.checked)}
              style={{ width: 'auto' }}
            />
            <span>Repeats</span>
          </label>

          {repeats && (
            <>
              <div className="row" style={{ alignItems: 'flex-end', gap: '0.75rem', marginTop: '0.75rem' }}>
                <div>
                  <label htmlFor="ls-freq">How often</label>
                  <select
                    id="ls-freq"
                    value={frequency}
                    onChange={(e) => {
                      const next = e.target.value as 'daily' | 'weekly' | 'monthly';
                      setFrequency(next);
                      // Weekdays mean nothing outside a weekly rule, and the
                      // server refuses them there rather than ignoring them.
                      if (next !== 'weekly') setWeekdays([]);
                    }}
                    style={{ width: 'auto' }}
                  >
                    <option value="daily">Daily</option>
                    <option value="weekly">Weekly</option>
                    <option value="monthly">Monthly</option>
                  </select>
                </div>
                <div>
                  <label htmlFor="ls-interval">Every</label>
                  <select
                    id="ls-interval"
                    value={interval}
                    onChange={(e) => setInterval(e.target.value)}
                    style={{ width: 'auto' }}
                  >
                    {[1, 2, 3, 4].map((n) => (
                      <option key={n} value={String(n)}>
                        {n === 1
                          ? frequency === 'daily'
                            ? 'day'
                            : frequency === 'weekly'
                              ? 'week'
                              : 'month'
                          : `${String(n)} ${frequency === 'daily' ? 'days' : frequency === 'weekly' ? 'weeks' : 'months'}`}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label htmlFor="ls-count">Classes</label>
                  <input
                    id="ls-count"
                    type="number"
                    min={2}
                    max={200}
                    value={count}
                    onChange={(e) => setCount(e.target.value)}
                  />
                </div>
              </div>

              {frequency === 'weekly' && (
                <div style={{ marginTop: '0.75rem' }}>
                  <label>On which days?</label>
                  <div className="button-row" style={{ flexWrap: 'wrap' }}>
                    {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((name, day) => (
                      <button
                        key={name}
                        type="button"
                        className={weekdays.includes(day) ? 'button-primary' : ''}
                        onClick={() =>
                          setWeekdays((current) =>
                            current.includes(day)
                              ? current.filter((d) => d !== day)
                              : [...current, day],
                          )
                        }
                      >
                        {name}
                      </button>
                    ))}
                  </div>
                  <p className="field-hint">
                    Leave all unselected to repeat on the same weekday as the start date.
                  </p>
                </div>
              )}

              {/*
                The rule in words. The only defence against a term that is subtly
                wrong: three controls are not checkable at a glance, and nobody
                verifies a mistake by reading twelve dates afterwards.
              */}
              <p className="field-hint" style={{ marginTop: '0.5rem', color: 'var(--accent)' }}>
                {describeRepeat(frequency, Number(interval), weekdays, Number(count), startsAt)}
              </p>
            </>
          )}
        </div>

        <div>
          <label htmlFor="ls-description">Description</label>
          <input
            id="ls-description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            maxLength={2000}
          />
        </div>

        <div className="button-row">
          <button type="submit" className="button-primary" disabled={pending}>
            {pending && <Spinner />}
            Schedule
          </button>
          <button
            type="button"
            className="button-secondary"
            disabled={pending}
            onClick={() => setOpen(false)}
          >
            Cancel
          </button>
        </div>
      </form>
    </Card>
  );
}

/** The next whole hour, as a datetime-local value in the browser's zone. */
function defaultStart(): string {
  const next = new Date();
  next.setMinutes(0, 0, 0);
  next.setHours(next.getHours() + 1);
  const pad = (n: number): string => String(n).padStart(2, '0');
  return `${String(next.getFullYear())}-${pad(next.getMonth() + 1)}-${pad(next.getDate())}T${pad(
    next.getHours(),
  )}:${pad(next.getMinutes())}`;
}

/* ---------------------------------------------------------------- provider */

const ZOOM_FIELDS: ReadonlyArray<{ name: string; label: string; secret: boolean }> = [
  { name: 'accountId', label: 'Account ID', secret: false },
  { name: 'clientId', label: 'Client ID', secret: false },
  { name: 'clientSecret', label: 'Client secret', secret: true },
  { name: 'webhookSecretToken', label: 'Webhook secret token', secret: true },
];

/**
 * Zoom account setup.
 *
 * Credentials are write-only: what comes back is a masked hint, never the value.
 * Rotation replaces the whole bundle, which is why the form asks for every field
 * again rather than pre-filling anything.
 */
function ProviderCard({ orgId }: { orgId: string }): ReactNode {
  const credentials = useAsync(
    () => api.get<{ data: MeetingCredential[] }>(`/api/organizations/${orgId}/meeting-credentials`),
    [orgId],
  );
  const { run, pending, error } = useAction();
  const [open, setOpen] = useState(false);
  const [values, setValues] = useState<Record<string, string>>({});

  const rows = credentials.data?.data ?? [];

  async function save(event: FormEvent): Promise<void> {
    event.preventDefault();
    await run(async () => {
      await api.post(`/api/organizations/${orgId}/meeting-credentials`, {
        provider: 'zoom',
        label: 'Zoom account',
        credentials: values,
        activate: true,
      });
      setValues({});
      setOpen(false);
      credentials.reload();
    });
  }

  async function test(id: string): Promise<void> {
    await run(async () => {
      await api.post(`/api/organizations/${orgId}/meeting-credentials/${id}/test`);
      credentials.reload();
    });
  }

  async function remove(id: string): Promise<void> {
    await run(async () => {
      await api.delete(`/api/organizations/${orgId}/meeting-credentials/${id}`);
      credentials.reload();
    });
  }

  return (
    <Card title="Zoom account">
      <ErrorBanner error={credentials.error} />
      <ErrorBanner message={error} />

      <p className="small faint" style={{ marginBottom: '0.75rem' }}>
        Sessions are created in your own Zoom account. Add the credentials of a Server-to-Server
        OAuth app; secrets are encrypted and cannot be read back.
      </p>

      {rows.length > 0 && (
        <table>
          <thead>
            <tr>
              <th>Account</th>
              <th>Status</th>
              <th>Last tested</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={row.id}>
                <td>
                  {row.label}
                  <div className="faint mono small">{row.credentialHints['accountId']}</div>
                </td>
                <td>
                  {row.active ? (
                    <span className="badge badge-success">Active</span>
                  ) : (
                    <span className="badge">Inactive</span>
                  )}
                </td>
                <td className="muted small">
                  {row.lastTestedAt === null ? (
                    'Never'
                  ) : row.lastTestOk ? (
                    `Worked ${formatRelative(row.lastTestedAt)}`
                  ) : (
                    <span className="badge badge-danger">Failed</span>
                  )}
                </td>
                <td style={{ textAlign: 'right' }}>
                  <div className="button-row" style={{ justifyContent: 'flex-end' }}>
                    <button
                      type="button"
                      className="button-ghost"
                      disabled={pending || !row.active}
                      onClick={() => void test(row.id)}
                    >
                      Test
                    </button>
                    <button
                      type="button"
                      className="button-danger"
                      disabled={pending}
                      onClick={() => void remove(row.id)}
                    >
                      Remove
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {rows.some((row) => row.lastTestOk === false && row.lastTestError) && (
        <p className="field-hint">{rows.find((row) => row.lastTestOk === false)?.lastTestError}</p>
      )}

      {open ? (
        <form onSubmit={(e) => void save(e)} className="stack" style={{ marginTop: '1rem' }}>
          {ZOOM_FIELDS.map((field) => (
            <div key={field.name}>
              <label htmlFor={`zoom-${field.name}`}>{field.label}</label>
              <input
                id={`zoom-${field.name}`}
                type={field.secret ? 'password' : 'text'}
                value={values[field.name] ?? ''}
                onChange={(e) => setValues({ ...values, [field.name]: e.target.value })}
                autoComplete="off"
                required
              />
            </div>
          ))}
          <p className="field-hint">
            The webhook secret token is not the client secret. After saving, add a webhook in the
            same Zoom app for <code>meeting.started</code> and <code>meeting.ended</code> pointing
            at the URL shown here.
          </p>
          <div className="button-row">
            <button type="submit" className="button-primary" disabled={pending}>
              {pending && <Spinner />}
              Save credentials
            </button>
            <button type="button" className="button-secondary" onClick={() => setOpen(false)}>
              Cancel
            </button>
          </div>
        </form>
      ) : (
        <div className="button-row" style={{ marginTop: '0.75rem' }}>
          <button type="button" className="button-secondary" onClick={() => setOpen(true)}>
            {rows.length === 0 ? 'Connect Zoom' : 'Add another account'}
          </button>
        </div>
      )}

      {rows.length > 0 && (
        <div style={{ marginTop: '1rem' }}>
          <p className="field-hint">
            Webhook URL for the active account — add it in your Zoom app so “Live now” reflects what
            the host actually did, not just the calendar.
          </p>
          <CopyLink
            value={`${window.location.origin}/api/webhooks/meetings/${
              rows.find((row) => row.active)?.id ?? rows[0]?.id ?? ''
            }`}
            label="Copy URL"
          />
        </div>
      )}
    </Card>
  );
}
